import { useSyncExternalStore } from 'react';
import AssistantResource, { type InboxItem } from 'Resources/AssistantChat';

// Inbox badge count for the floating chat. Fetches only while it has a subscriber, so users without floating-chat access (who never mount a consumer) never call getInbox().

const POLL_MS = 60_000;

let count = 0;
let active = true;
let timer: ReturnType<typeof setInterval> | null = null;
// Bumped on every count update; a slow getInbox that resolves after a newer
// update (e.g. a poll landing just after a mark-as-read) sees a stale
// generation and is discarded, so it can't push a stale count back up.
let generation = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

const apply = (items: InboxItem[]): void => {
	// The badge counts everything except already-seen ("earlier") rows.
	const next = items.filter(i => i.kind !== 'earlier').length;
	if (next !== count) {
		count = next;
		emit();
	}
};

// Owning the count rule here lets any consumer that already holds fresh inbox
// items push the count in (authoritatively, superseding an in-flight refresh)
// instead of triggering a second getInbox fetch.
function setFromItems(items: InboxItem[]): void {
	generation += 1;
	apply(items);
}

function refresh(): void {
	const gen = ++generation;
	AssistantResource.getInbox()
		.then(items => {
			if (gen === generation) apply(items);
		})
		.catch(err => {
			// Console only, never a toast: nothing the user did triggered this, and at one tick a
			// minute per tab an outage would otherwise become an endless stream of notifications.
			// The user-initiated inbox views (WorkspaceInbox, WorkspaceSidebar) still surface theirs.
			console.error('[AIWorkspace:getInbox] inbox badge poll failed', err);
			// On 401 the session is gone and Resource._handleError is already redirecting to /login;
			// clear the badge so it doesn't sit frozen at a stale count. Polling deliberately
			// continues — re-login is in-SPA with no page reload, so anything latched here would
			// never reset and the badge would read 0 for the rest of the tab's life.
			if ((err as { response?: { status?: number } })?.response?.status === 401) {
				if (gen === generation) apply([]);
			}
		});
}

function startTimer(): void {
	if (timer || !active || listeners.size === 0) return;
	timer = setInterval(refresh, POLL_MS);
}

function stopTimer(): void {
	if (!timer) return;
	clearInterval(timer);
	timer = null;
}

function subscribe(cb: () => void): () => void {
	const first = listeners.size === 0;
	listeners.add(cb);
	if (first) refresh(); // immediate load when the first consumer mounts
	startTimer();
	return () => {
		listeners.delete(cb);
		if (listeners.size === 0) stopTimer();
	};
}

export const inboxCountStore = {
	subscribe,
	getSnapshot: (): number => count,
	refresh,
	setFromItems,
	// Pause the poll while the floating window is open (it drives its own refreshes); resume with an immediate refresh on close.
	setActive(next: boolean): void {
		if (next === active) return;
		active = next;
		if (active) {
			startTimer();
			if (listeners.size > 0) refresh();
		} else {
			stopTimer();
		}
	}
};

export function useInboxCount(): number {
	return useSyncExternalStore(inboxCountStore.subscribe, inboxCountStore.getSnapshot);
}
